import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-dark-primary",
        defaultRounded:
          "bg-primary text-primary-foreground hover:bg-dark-primary text-base rounded-full",
        blackRounded: "bg-black text-white text-base rounded-full",
        greenRounded: "bg-[#5FA443] text-white text-base rounded-full",
        lightBlueRounded: "bg-[#99C0D3] text-white text-base rounded-full",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        action: "bg-action text-primary-foreground hover:bg-dark-action",
        green: "bg-green-600 text-primary-foreground hover:bg-green-700",
        yellow: "bg-yellow-600 text-primary-foreground hover:bg-yellow-700",
        pink: "bg-pink-600 text-primary-foreground hover:bg-pink-700",
        actionRounded:
          "bg-action rounded-3xl text-primary-foreground hover:bg-dark-action",
        outline:
          "border border-input hover:bg-secondary hover:text-accent-foreground",
        customOutline:
          "border-2 border-primary text-primary hover:bg-primary hover:text-white",
        customImpactGreenOutlineRounded:
          "border-2 rounded-3xl border-impact-green text-impact-green hover:bg-impact-green hover:text-white",
        customOutlineRounded:
          "border-2 rounded-3xl border-primary text-primary hover:bg-primary hover:text-white",
        customBlueOutline:
          "border-2 border-[#006291] hover:bg-[#006291] text-[#006291] hover:text-white rounded-3xl font-semibold",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        success: "bg-success text-primary-foreground hover:bg-dark-success",
        successRounded:
          "bg-success rounded-3xl text-primary-foreground hover:bg-dark-success",
        dangerRounded:
          "bg-destructive rounded-3xl text-primary-foreground hover:bg-destructive/90",
        card: "text-gray-500 border border-gray-300 hover:border-gray-500	hover:text-gray-700	shadow-md hover:shadow-lg",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        ghostImpactGreen:
          "hover:bg-impact-green hover:text-impact-green-foreground",
        link: "underline-offset-4 hover:underline text-primary",
        dark: "bg-[#3A3E45] hover:bg-[#1a1b1f] text-[#FFD9B0] rounded-3xl font-semibold",
        web: "bg-[#E57200] hover:bg-[#a35303] text-white rounded-3xl font-semibold",
        webBlue: "bg-[#99C0D3] hover:bg-[#58798a] text-white rounded-3xl font-semibold",
        beige:
          "bg-[#FFD4A1] hover:bg-[#f2ab57] text-[#3A3E45] rounded-3xl font-semibold",
        impactGreen:
          "bg-[#91A77C] text-[#3A3E45] rounded-3xl font-semibold hover:bg-black hover:text-white",
        impactBlack: "bg-[#3A3E45] text-[#91A77C] rounded-3xl font-semibold",
        orange:
          "bg-[#E57200] hover:bg-[#7d3f02] text-white rounded-3xl font-semibold",
      },
      size: {
        default: "py-2 px-4",
        sm: "h-9 px-3 rounded-md",
        lg: "h-11 px-8 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
